/**
 * Self-test suite for profile-share consent + prompt policy.
 *
 * Mirrors `kaether_for_ios/Stoat/E2EE/ProfileSharePromptSelfTest.swift`
 * case-for-case across five groups:
 *
 *   A. Record + store basics (8 cases): initial state, valid first
 *      transitions, idempotency, invalid markRevoked-from-undecided.
 *
 *   B. Full transition matrix (7 cases): every (state × action) cell
 *      verified against the documented contract.
 *
 *   C. Queries (4 cases): peersSharing / revokedPeers filtering and
 *      sort order; freshStore returns empty.
 *
 *   D. Prompt policy (7 cases): every rule branch exercised; rule
 *      priority (in-roster beats declined).
 *
 *   E. Hardening invariants (3 cases): policy context has NO
 *      server-supplied name field; markSharing stores a HASH only;
 *      persistence across store instances.
 *
 * Every case uses an isolated IndexedDB database name so the suite is
 * safe to run on every dev launch without polluting production state.
 *
 * Registered with the global SelfTestRunner singleton at module load
 * time. The runner auto-discovers this file via Vite `import.meta.glob`
 * in `selftest/index.ts` (matches `**\/*SelfTest.ts`).
 */

import { SelfTestRunner, buildSuiteResult, fail, pass } from "../selftest";
import type { TestCase } from "../selftest";
import type { ProfileSharePromptContext } from "./ProfileSharePromptPolicy";
import { ProfileSharePromptPolicy } from "./ProfileSharePromptPolicy";
import { ProfileShareStore } from "./ProfileShareStore";

// ---------------------------------------------------------------------------
// Fixture helpers
// ---------------------------------------------------------------------------

let dbCounter = 0;

/** Suite-wide unique tag so DB names do not collide across dev launches
 *  (IndexedDB persists across page reloads — a counter alone reused stale
 *  state from a prior run, defeating "fresh" isolation). */
const dbSuiteTag =
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

/** Create a fresh isolated store for each test. */
function freshStore(): ProfileShareStore {
  dbCounter += 1;
  return new ProfileShareStore(`kaether:profile-share:test:${dbSuiteTag}:${dbCounter}`);
}

/** Generate random profile-key bytes (32 bytes). */
function makeProfileKey(): Uint8Array {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return bytes;
}

/** Compute SHA-256 hex of bytes (same algorithm as the store). */
async function sha256Hex(bytes: Uint8Array): Promise<string> {
  const buf = await crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

// ---------------------------------------------------------------------------
// A. Record + store basics
// ---------------------------------------------------------------------------

async function testFreshStoreDecisionIsUndecided(): Promise<TestCase> {
  const name = "fresh store reports 'undecided' + undefined record";
  const s = freshStore();
  const decision = await s.getDecision("alice");
  const record = await s.getRecord("alice");
  const ok = decision === "undecided" && record === undefined;
  return ok
    ? pass(name)
    : fail(name, `decision=${decision} record=${JSON.stringify(record)}`);
}

async function testMarkSharingFromUndecided(): Promise<TestCase> {
  const name = "markSharing from 'undecided' records sharing + hash";
  const s = freshStore();
  const key = makeProfileKey();
  const result = await s.markSharing("alice", key);
  if (!result.ok) return fail(name, `unexpected error: ${JSON.stringify(result.error)}`);
  const expectedHash = await sha256Hex(key);
  const r = result.record;
  const ok =
    r.decision === "sharing" &&
    r.peerUserId === "alice" &&
    r.sharedProfileKeyHash === expectedHash &&
    r.revokedAt === null;
  return ok ? pass(name) : fail(name, `got ${JSON.stringify(r)}`);
}

async function testMarkSharingFromDeclined(): Promise<TestCase> {
  const name = "markSharing from 'declined' succeeds (user changed mind)";
  const s = freshStore();
  await s.markDeclined("alice");
  const key = makeProfileKey();
  const result = await s.markSharing("alice", key);
  const ok = result.ok && result.record.decision === "sharing";
  return ok ? pass(name) : fail(name, `result=${JSON.stringify(result)}`);
}

async function testMarkSharingFromSharingIsIdempotent(): Promise<TestCase> {
  const name = "markSharing from 'sharing' is idempotent (no churn)";
  const s = freshStore();
  const key = makeProfileKey();
  const originalTs = "2024-11-14T00:00:00.000Z";
  await s.markSharing("alice", key, originalTs);
  const firstRecord = await s.getRecord("alice");

  const laterTs = "2024-11-14T02:46:39.000Z";
  const secondKey = makeProfileKey();
  const result = await s.markSharing("alice", secondKey, laterTs);

  const ok =
    firstRecord !== undefined &&
    result.ok &&
    result.record.decidedAt === originalTs &&
    result.record.sharedProfileKeyHash === firstRecord.sharedProfileKeyHash &&
    result.record.decision === "sharing";
  return ok
    ? pass(name)
    : fail(
        name,
        `first=${JSON.stringify(firstRecord)} second=${JSON.stringify(result.ok ? result.record : result.error)}`,
      );
}

async function testMarkDeclinedFromUndecided(): Promise<TestCase> {
  const name = "markDeclined from 'undecided' records declined";
  const s = freshStore();
  const result = await s.markDeclined("alice");
  const ok = result.ok && result.record.decision === "declined";
  return ok ? pass(name) : fail(name, `result=${JSON.stringify(result)}`);
}

async function testMarkDeclinedFromDeclinedIsIdempotent(): Promise<TestCase> {
  const name = "markDeclined from 'declined' is idempotent";
  const s = freshStore();
  const firstTs = "2024-11-14T00:00:00.000Z";
  await s.markDeclined("alice", firstTs);
  const firstRecord = await s.getRecord("alice");
  const laterTs = "2024-11-25T07:33:19.000Z";
  await s.markDeclined("alice", laterTs);
  const secondRecord = await s.getRecord("alice");
  const ok =
    firstRecord !== undefined &&
    secondRecord !== undefined &&
    firstRecord.decidedAt === secondRecord.decidedAt;
  return ok
    ? pass(name)
    : fail(
        name,
        `first=${JSON.stringify(firstRecord)} second=${JSON.stringify(secondRecord)}`,
      );
}

async function testMarkRevokedFromSharing(): Promise<TestCase> {
  const name = "markRevoked from 'sharing' succeeds with revokedAt set";
  const s = freshStore();
  const key = makeProfileKey();
  await s.markSharing("alice", key);
  const revokeTs = "2024-11-14T10:53:20.000Z";
  const result = await s.markRevoked("alice", revokeTs);
  if (!result.ok) return fail(name, `unexpected error: ${JSON.stringify(result.error)}`);
  const r = result.record;
  const ok =
    r.decision === "revoked" &&
    r.revokedAt === revokeTs &&
    r.decidedAt === revokeTs;
  return ok ? pass(name) : fail(name, `got ${JSON.stringify(r)}`);
}

async function testMarkRevokedFromUndecidedRejected(): Promise<TestCase> {
  const name = "markRevoked from 'undecided' is invalidTransition";
  const s = freshStore();
  const result = await s.markRevoked("alice");
  const ok =
    !result.ok &&
    result.error.kind === "invalidTransition" &&
    result.error.from === "undecided" &&
    result.error.action === "markRevoked";
  return ok ? pass(name) : fail(name, `result=${JSON.stringify(result)}`);
}

// ---------------------------------------------------------------------------
// B. Full transition matrix
// ---------------------------------------------------------------------------

async function testTransitionsFromUndecided(): Promise<TestCase> {
  const name =
    "transitions from 'undecided': sharing/declined OK, revoked rejected";
  const key = makeProfileKey();

  const s1 = freshStore();
  const ok1 = (await s1.markSharing("a", key)).ok === true;

  const s2 = freshStore();
  const ok2 = (await s2.markDeclined("a")).ok === true;

  const s3 = freshStore();
  const r3 = await s3.markRevoked("a");
  const ok3 =
    !r3.ok &&
    r3.error.kind === "invalidTransition" &&
    r3.error.from === "undecided";

  const ok = ok1 && ok2 && ok3;
  return ok ? pass(name) : fail(name, `ok1=${ok1} ok2=${ok2} ok3=${ok3}`);
}

async function testTransitionsFromSharingMarkSharing(): Promise<TestCase> {
  const name = "transitions from 'sharing': markSharing is idempotent success";
  const s = freshStore();
  const key = makeProfileKey();
  await s.markSharing("a", key);
  const r = await s.markSharing("a", key);
  const ok = r.ok && r.record.decision === "sharing";
  return ok ? pass(name) : fail(name, `result=${JSON.stringify(r)}`);
}

async function testTransitionsFromSharingMarkDeclined(): Promise<TestCase> {
  const name = "transitions from 'sharing': markDeclined OK";
  const s = freshStore();
  const key = makeProfileKey();
  await s.markSharing("a", key);
  const r = await s.markDeclined("a");
  const ok = r.ok && r.record.decision === "declined";
  return ok ? pass(name) : fail(name, `result=${JSON.stringify(r)}`);
}

async function testTransitionsFromSharingMarkRevoked(): Promise<TestCase> {
  const name = "transitions from 'sharing': markRevoked OK";
  const s = freshStore();
  const key = makeProfileKey();
  await s.markSharing("a", key);
  const r = await s.markRevoked("a");
  const ok = r.ok && r.record.decision === "revoked";
  return ok ? pass(name) : fail(name, `result=${JSON.stringify(r)}`);
}

async function testTransitionsFromDeclined(): Promise<TestCase> {
  const name =
    "transitions from 'declined': sharing OK, declined idempotent, revoked rejected";
  const key = makeProfileKey();

  const s1 = freshStore();
  await s1.markDeclined("a");
  const ok1 = (await s1.markSharing("a", key)).ok === true;

  const s2 = freshStore();
  await s2.markDeclined("a");
  const ok2 = (await s2.markDeclined("a")).ok === true;

  const s3 = freshStore();
  await s3.markDeclined("a");
  const r3 = await s3.markRevoked("a");
  const ok3 =
    !r3.ok &&
    r3.error.kind === "invalidTransition" &&
    r3.error.from === "declined";

  const ok = ok1 && ok2 && ok3;
  return ok ? pass(name) : fail(name, `ok1=${ok1} ok2=${ok2} ok3=${ok3}`);
}

async function testTransitionsFromRevokedMarkSharing(): Promise<TestCase> {
  const name = "transitions from 'revoked': markSharing re-enrols with new key hash";
  const s = freshStore();
  const key1 = makeProfileKey();
  await s.markSharing("a", key1);
  await s.markRevoked("a");
  const key2 = makeProfileKey();
  const r = await s.markSharing("a", key2);
  if (!r.ok) return fail(name, `unexpected error: ${JSON.stringify(r.error)}`);
  const expectedHash = await sha256Hex(key2);
  const ok =
    r.record.decision === "sharing" &&
    r.record.sharedProfileKeyHash === expectedHash &&
    r.record.revokedAt === null;
  return ok ? pass(name) : fail(name, `got ${JSON.stringify(r.record)}`);
}

async function testTransitionsFromRevokedMarkDeclinedAndMarkRevoked(): Promise<TestCase> {
  const name =
    "transitions from 'revoked': declined and revoked both invalidTransition";
  const s = freshStore();
  const key = makeProfileKey();
  await s.markSharing("a", key);
  await s.markRevoked("a");

  const r1 = await s.markDeclined("a");
  const ok1 =
    !r1.ok &&
    r1.error.kind === "invalidTransition" &&
    r1.error.from === "revoked";

  const r2 = await s.markRevoked("a");
  const ok2 =
    !r2.ok &&
    r2.error.kind === "invalidTransition" &&
    r2.error.from === "revoked";

  const ok = ok1 && ok2;
  return ok ? pass(name) : fail(name, `r1=${JSON.stringify(r1)} r2=${JSON.stringify(r2)}`);
}

// ---------------------------------------------------------------------------
// C. Queries
// ---------------------------------------------------------------------------

async function testPeersSharingOnlySharing(): Promise<TestCase> {
  const name = "peersSharing returns only 'sharing' records";
  const s = freshStore();
  const k = makeProfileKey();
  await s.markSharing("a", k);
  await s.markDeclined("b");
  await s.markSharing("c", k);
  await s.markRevoked("c"); // c is now revoked
  const sharing = await s.peersSharing();
  const ids = new Set(sharing.map((r) => r.peerUserId));
  const ok = ids.size === 1 && ids.has("a");
  return ok ? pass(name) : fail(name, `got ${JSON.stringify([...ids])}`);
}

async function testRevokedPeersOnlyRevoked(): Promise<TestCase> {
  const name = "revokedPeers returns only 'revoked' records";
  const s = freshStore();
  const k = makeProfileKey();
  await s.markSharing("a", k);
  await s.markSharing("b", k);
  await s.markRevoked("b");
  await s.markDeclined("c");
  const revoked = await s.revokedPeers();
  const ids = new Set(revoked.map((r) => r.peerUserId));
  const ok = ids.size === 1 && ids.has("b");
  return ok ? pass(name) : fail(name, `got ${JSON.stringify([...ids])}`);
}

async function testQueriesSortDecidedAtDesc(): Promise<TestCase> {
  const name = "peersSharing + revokedPeers sort decidedAt descending";
  const s = freshStore();
  const k = makeProfileKey();
  await s.markSharing("old", k, "1970-01-01T00:16:40.000Z"); // ts=1000
  await s.markSharing("mid", k, "1970-01-01T00:33:20.000Z"); // ts=2000
  await s.markSharing("new", k, "1970-01-01T00:50:00.000Z"); // ts=3000

  const sharingOrder = (await s.peersSharing()).map((r) => r.peerUserId);
  const sharingOk =
    sharingOrder[0] === "new" &&
    sharingOrder[1] === "mid" &&
    sharingOrder[2] === "old";

  // Set up revoked pairs
  await s.markSharing("rOld", k, "1970-01-01T00:01:40.000Z"); // ts=100
  await s.markRevoked("rOld", "1970-01-01T01:06:40.000Z"); // ts=4000
  await s.markSharing("rNew", k, "1970-01-01T00:03:20.000Z"); // ts=200
  await s.markRevoked("rNew", "1970-01-01T01:23:20.000Z"); // ts=5000

  const revokedOrder = (await s.revokedPeers()).map((r) => r.peerUserId);
  const revokedOk = revokedOrder[0] === "rNew" && revokedOrder[1] === "rOld";

  const ok = sharingOk && revokedOk;
  return ok
    ? pass(name)
    : fail(
        name,
        `sharing=${JSON.stringify(sharingOrder)} revoked=${JSON.stringify(revokedOrder)}`,
      );
}

async function testFreshStoreHasEmptyQueries(): Promise<TestCase> {
  const name = "fresh store: peersSharing and revokedPeers are empty";
  const s = freshStore();
  const sharing = await s.peersSharing();
  const revoked = await s.revokedPeers();
  const ok = sharing.length === 0 && revoked.length === 0;
  return ok
    ? pass(name)
    : fail(name, `sharing=${sharing.length} revoked=${revoked.length}`);
}

// ---------------------------------------------------------------------------
// D. Prompt policy
// ---------------------------------------------------------------------------

function makeCtx(
  overrides: Partial<ProfileSharePromptContext>,
): ProfileSharePromptContext {
  return {
    peerUserId: "alice",
    isPeerInRoster: false,
    isPeerInSharedGroup: false,
    existingDecision: "undecided",
    isOutboundSendInProgress: true,
    ...overrides,
  };
}

async function testPolicyShowPromptWhenUndecidedAndAlone(): Promise<TestCase> {
  const name = "policy: undecided + non-contact + no group → 'showPrompt'";
  const d = ProfileSharePromptPolicy.decide(makeCtx({}));
  const ok = d === "showPrompt";
  return ok ? pass(name) : fail(name, `got ${d}`);
}

async function testPolicyInRosterSkips(): Promise<TestCase> {
  const name = "policy: in-roster → 'skipInRoster'";
  const d = ProfileSharePromptPolicy.decide(makeCtx({ isPeerInRoster: true }));
  const ok = d === "skipInRoster";
  return ok ? pass(name) : fail(name, `got ${d}`);
}

async function testPolicyInSharedGroupSkips(): Promise<TestCase> {
  const name = "policy: shared-group co-member → 'skipCoMember'";
  const d = ProfileSharePromptPolicy.decide(
    makeCtx({ isPeerInSharedGroup: true }),
  );
  const ok = d === "skipCoMember";
  return ok ? pass(name) : fail(name, `got ${d}`);
}

async function testPolicySharingSkips(): Promise<TestCase> {
  const name = "policy: already 'sharing' → 'skipAlreadyShared'";
  const d = ProfileSharePromptPolicy.decide(
    makeCtx({ existingDecision: "sharing" }),
  );
  const ok = d === "skipAlreadyShared";
  return ok ? pass(name) : fail(name, `got ${d}`);
}

async function testPolicyDeclinedSkips(): Promise<TestCase> {
  const name = "policy: previously 'declined' → 'skipDeclined'";
  const d = ProfileSharePromptPolicy.decide(
    makeCtx({ existingDecision: "declined" }),
  );
  const ok = d === "skipDeclined";
  return ok ? pass(name) : fail(name, `got ${d}`);
}

async function testPolicyRevokedSkips(): Promise<TestCase> {
  const name = "policy: previously 'revoked' → 'skipRevoked'";
  const d = ProfileSharePromptPolicy.decide(
    makeCtx({ existingDecision: "revoked" }),
  );
  const ok = d === "skipRevoked";
  return ok ? pass(name) : fail(name, `got ${d}`);
}

async function testPolicyInRosterBeatsDeclined(): Promise<TestCase> {
  const name = "policy: in-roster wins over 'declined'";
  const d = ProfileSharePromptPolicy.decide(
    makeCtx({ isPeerInRoster: true, existingDecision: "declined" }),
  );
  const ok = d === "skipInRoster";
  return ok ? pass(name) : fail(name, `got ${d}`);
}

// ---------------------------------------------------------------------------
// E. Hardening invariants
// ---------------------------------------------------------------------------

async function testPolicyContextHasNoServerSuppliedName(): Promise<TestCase> {
  const name = "policy context exposes no server-supplied name field";
  // Assert the exact key set on ProfileSharePromptContext. A future
  // refactor adding a `peerClaimedName` field would make this fail,
  // preventing a silent server-influence regression.
  const ctx = makeCtx({});
  const actualKeys = new Set(Object.keys(ctx));
  const expectedKeys = new Set([
    "peerUserId",
    "isPeerInRoster",
    "isPeerInSharedGroup",
    "existingDecision",
    "isOutboundSendInProgress",
  ]);
  const keysMatch =
    actualKeys.size === expectedKeys.size &&
    [...expectedKeys].every((k) => actualKeys.has(k));
  return keysMatch
    ? pass(name)
    : fail(
        name,
        `expected=${JSON.stringify([...expectedKeys].sort())} got=${JSON.stringify([...actualKeys].sort())}`,
      );
}

async function testMarkSharingStoresHashNotKey(): Promise<TestCase> {
  const name = "markSharing stores hash, not raw profile-key bytes";
  const s = freshStore();
  const key = makeProfileKey();
  await s.markSharing("alice", key);
  const rec = await s.getRecord("alice");
  if (!rec) return fail(name, "no record after markSharing");

  const expectedHash = await sha256Hex(key);
  if (rec.sharedProfileKeyHash !== expectedHash) {
    return fail(name, `hash mismatch: expected ${expectedHash} got ${rec.sharedProfileKeyHash}`);
  }

  // The raw key must not appear in the JSON-serialised record.
  // Check: (a) the record has no 'key' field, (b) the raw bytes aren't
  // present anywhere in the serialised JSON (as hex or base64).
  const hasKeyField = Object.prototype.hasOwnProperty.call(rec, "key");
  if (hasKeyField) return fail(name, "record has 'key' field — raw key leaked");

  const blob = JSON.stringify(rec);
  const rawHex = Array.from(key)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
  const rawB64 = btoa(String.fromCharCode(...key));
  const containsRaw =
    blob.includes(rawHex) || blob.includes(rawB64);
  return containsRaw
    ? fail(name, `serialised record contains raw key bytes (hex or base64): ${blob}`)
    : pass(name);
}

async function testPersistenceAcrossInstances(): Promise<TestCase> {
  const name = "persistence: decisions survive a fresh store instance";
  // Use a shared DB name so both store instances read/write the same DB.
  // Tag with the suite-wide UUID so a prior dev-launch's residual records
  // (IndexedDB is persistent) cannot pollute this test.
  dbCounter += 1;
  const sharedDbName = `kaether:profile-share:persist-test:${dbSuiteTag}:${dbCounter}`;

  const writer = new ProfileShareStore(sharedDbName);
  const key = makeProfileKey();
  await writer.markSharing("alice", key);
  await writer.markDeclined("bob");

  const reader = new ProfileShareStore(sharedDbName);
  const aliceRec = await reader.getRecord("alice");
  const bobRec = await reader.getRecord("bob");
  const expectedAliceHash = await sha256Hex(key);

  const ok =
    aliceRec?.decision === "sharing" &&
    aliceRec.sharedProfileKeyHash === expectedAliceHash &&
    bobRec?.decision === "declined";
  return ok
    ? pass(name)
    : fail(
        name,
        `alice=${JSON.stringify(aliceRec)} bob=${JSON.stringify(bobRec)}`,
      );
}

// ---------------------------------------------------------------------------
// Suite registration
// ---------------------------------------------------------------------------

SelfTestRunner.register({
  name: "Profile share prompt (non-contact first-message)",
  isVerified: true,
  run: async (): Promise<ReturnType<typeof buildSuiteResult>> => {
    const cases: TestCase[] = await Promise.all([
      // A. Record + store basics
      testFreshStoreDecisionIsUndecided(),
      testMarkSharingFromUndecided(),
      testMarkSharingFromDeclined(),
      testMarkSharingFromSharingIsIdempotent(),
      testMarkDeclinedFromUndecided(),
      testMarkDeclinedFromDeclinedIsIdempotent(),
      testMarkRevokedFromSharing(),
      testMarkRevokedFromUndecidedRejected(),
      // B. Full transition matrix
      testTransitionsFromUndecided(),
      testTransitionsFromSharingMarkSharing(),
      testTransitionsFromSharingMarkDeclined(),
      testTransitionsFromSharingMarkRevoked(),
      testTransitionsFromDeclined(),
      testTransitionsFromRevokedMarkSharing(),
      testTransitionsFromRevokedMarkDeclinedAndMarkRevoked(),
      // C. Queries
      testPeersSharingOnlySharing(),
      testRevokedPeersOnlyRevoked(),
      testQueriesSortDecidedAtDesc(),
      testFreshStoreHasEmptyQueries(),
      // D. Prompt policy
      testPolicyShowPromptWhenUndecidedAndAlone(),
      testPolicyInRosterSkips(),
      testPolicyInSharedGroupSkips(),
      testPolicySharingSkips(),
      testPolicyDeclinedSkips(),
      testPolicyRevokedSkips(),
      testPolicyInRosterBeatsDeclined(),
      // E. Hardening invariants
      testPolicyContextHasNoServerSuppliedName(),
      testMarkSharingStoresHashNotKey(),
      testPersistenceAcrossInstances(),
    ]);
    return buildSuiteResult(cases);
  },
});
